import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { ApplicationSettingsTabId } from '../../../store/application-settings-tab-id';
import { useProxyStore } from '../../../store/proxy-store';
import {
  migrateLegacyTabSectionIfNeeded,
  TAB_SECTION_SCOPES,
  useTabSectionState,
} from '../../../store/tabSections';
import type { SettingsNavItemConfig } from '@terra/ui-design-system';
import type { SettingsSubTabItem } from '../layout/SettingsSubTabs';
import {
  findTargetSettingsNavGroup,
  getVisibleTargetSettingsNavGroups,
  resolveTargetSettingsGroupEntrySection,
  resolveVisibleTargetSettingsSection,
  TARGET_SETTINGS_DEFAULT_SECTION,
} from './targetSettingsNav';

export function useTargetSettingsSection({
  tabId,
  isAiApplicationTarget,
  isMcpServersEnabled,
  hasApplicationEligibleMcpServers,
  isMcpServerTarget,
  appTargetId,
  isPreventionEnabled,
  canDeleteApplication,
}: {
  tabId: string;
  isAiApplicationTarget: boolean;
  isMcpServersEnabled: boolean;
  hasApplicationEligibleMcpServers: boolean;
  isMcpServerTarget: boolean;
  appTargetId: string | undefined;
  isPreventionEnabled: boolean;
  canDeleteApplication: boolean;
}) {
  const tNav = useTranslations('settings.settingsNav');
  const { activeSection, setActiveSection } =
    useTabSectionState<ApplicationSettingsTabId>({
      scope: TAB_SECTION_SCOPES.applicationSettings,
      tabId,
      defaultSection: TARGET_SETTINGS_DEFAULT_SECTION,
    });
  const activeApplicationSettingsTab = useProxyStore(
    (state) => state.activeApplicationSettingsTab,
  );

  useEffect(() => {
    migrateLegacyTabSectionIfNeeded({
      scope: TAB_SECTION_SCOPES.applicationSettings,
      tabId,
      legacySection: useProxyStore.getState().activeApplicationSettingsTab,
      defaultSection: TARGET_SETTINGS_DEFAULT_SECTION,
      clearLegacySection: () =>
        useProxyStore
          .getState()
          .setActiveApplicationSettingsTab(TARGET_SETTINGS_DEFAULT_SECTION),
    });
  }, [tabId]);

  useEffect(() => {
    if (!isMcpServersEnabled && activeSection === 'mcp-servers') {
      setActiveSection(TARGET_SETTINGS_DEFAULT_SECTION);
    }
  }, [activeSection, isMcpServersEnabled, setActiveSection]);

  useEffect(() => {
    if (
      !isMcpServerTarget &&
      (activeSection === 'mcp-connection' ||
        activeApplicationSettingsTab === 'mcp-connection')
    ) {
      setActiveSection(TARGET_SETTINGS_DEFAULT_SECTION);
    }
  }, [
    activeApplicationSettingsTab,
    activeSection,
    isMcpServerTarget,
    setActiveSection,
  ]);

  useEffect(() => {
    if (
      activeApplicationSettingsTab === 'mcp-connection' &&
      isMcpServerTarget
    ) {
      setActiveSection('mcp-connection');
    }
  }, [activeApplicationSettingsTab, isMcpServerTarget, setActiveSection]);

  const visibility = useMemo(
    () => ({
      isAiApplicationTarget,
      isMcpServersEnabled,
      hasApplicationEligibleMcpServers,
      isMcpServerTarget,
      hasAppTargetId: Boolean(appTargetId),
      isPreventionEnabled,
      canDeleteApplication,
    }),
    [
      appTargetId,
      canDeleteApplication,
      hasApplicationEligibleMcpServers,
      isAiApplicationTarget,
      isMcpServerTarget,
      isMcpServersEnabled,
      isPreventionEnabled,
    ],
  );
  const visibleSection = resolveVisibleTargetSettingsSection({
    activeSection,
    visibility,
  });
  const groups = useMemo(
    () => getVisibleTargetSettingsNavGroups(visibility),
    [visibility],
  );
  const activeGroup = findTargetSettingsNavGroup({
    groups,
    section: visibleSection,
  });
  const navItems = useMemo<SettingsNavItemConfig[]>(
    () =>
      groups.map((group) => ({
        value: group.value,
        label: tNav(`target.${group.labelKey}`),
      })),
    [groups, tNav],
  );
  const subTabs = useMemo<SettingsSubTabItem<ApplicationSettingsTabId>[]>(
    () =>
      activeGroup && activeGroup.sections.length > 1
        ? activeGroup.sections.map((section) => ({
            value: section.value,
            label: tNav(`target.${section.labelKey}`),
            tooltip: section.tooltipKey
              ? tNav(`target.${section.tooltipKey}`)
              : undefined,
          }))
        : [],
    [activeGroup, tNav],
  );
  const selectGroup = (groupId: string) => {
    const entrySection = resolveTargetSettingsGroupEntrySection({
      groups,
      groupId,
    });

    if (entrySection) {
      setActiveSection(entrySection);
    }
  };

  return {
    visibleSection,
    setActiveSection,
    activeGroupId: activeGroup?.value ?? TARGET_SETTINGS_DEFAULT_SECTION,
    selectGroup,
    navItems,
    subTabs,
  };
}
